<template>
  <div ref="elementRef" v-style:bg="backgroundColor" class="md:flex">
    <div class="relative flex shrink-0" :class="mediaSize[imageWidth]">
      <cms-base-caption v-if="hasCaption" v-bind="{ captionColor, captionStyle, captionPosition, captionText }" />
      <div v-if="responsiveMediaList.length > 0" class="grid cols-2 w-full md:flex">
        <template v-for="(mediaObject, index) in responsiveMediaList">
          <base-picture
            v-if="mediaObject.images"
            :key="`picture-${index}`"
            v-bind="mediaObject.images"
            class="w-full"
            :class="getMediaClasses(index)"
            fit="cover"
            :lazy
          />
          <vf-video
            v-else-if="mediaObject.video"
            :key="`video-${index}`"
            v-bind="mediaObject.video"
            ref="videoRefs"
            :autoplay="!lazy"
            class="w-full"
            :class="getMediaClasses(index)"
            fit="cover"
            loop
            muted
          />
        </template>
      </div>

      <base-link
        v-if="mediaLink"
        aria-hidden="true"
        class="absolute-0"
        :data-cta="mediaLink.delocalizedTracking"
        tabindex="-1"
        :target="mediaLink.targetAttribute"
        :to="mediaLink.url"
        @click="handlePromotionClick('img')"
      />
    </div>
    <div class="w-full f-col" :class="contentAlignment">
      <div v-if="alignmentLarge.startsWith('3')" class="grid cols-2 <md:hidden">
        <template v-for="(mediaObject, index) in responsiveMediaList.slice(-2)">
          <base-picture
            v-if="mediaObject.images"
            :key="`picture-${index}`"
            v-bind="mediaObject.images"
            class="w-full"
            fit="cover"
            :lazy
          />
          <vf-video
            v-else-if="mediaObject.video"
            :key="`video-${index}`"
            v-bind="mediaObject.video"
            ref="videoRefs"
            :autoplay="!lazy"
            class="w-full"
            fit="cover"
            loop
            muted
          />
        </template>
      </div>
      <div
        class="w-full f-col grow px-4 py-6 space-y-2 lg:px-6 lg:space-y-4"
        :class="[
          textAlignment,
          {
            'order-first': alignmentLarge === '3-top-left' || alignmentLarge === '3-top-right',
          },
          verticalAlignmentClasses[verticalAlignment]]"
      >
        <div class="space-y-2 lg:space-y-4" :class="overlayWidth ? contentSize[overlayWidth] : ''">
          <div v-if="eyebrowText" v-style:c="eyebrowTextColor || subtitleColor" class="uppercase">
            {{ eyebrowText }}
          </div>
          <base-picture
            v-if="images"
            v-bind="images"
            class="max-h-38"
            fit="contain"
            :props-img="{ class: 'object-left' }"
          />
          <base-picture
            v-else-if="eyebrowLogo"
            :alt="eyebrowLogo.alt"
            class="max-h-38"
            fit="contain"
            :props-img="{ class: 'object-left' }"
            :src="eyebrowLogo.url"
          />
          <p v-if="overline" v-style:c="overlineColor" :class="overlineStyle" data-test-id="cms-banner-overline">
            {{ overline }}
          </p>
          <component
            :is="titleTag"
            v-if="title"
            v-style:c="titleColor"
            :class="[titleStyle, { 'sr-only': hideTitle }]"
            data-test-id="cms-banner-title"
          >
            <base-link
              v-if="mediaLink"
              :data-cta="mediaLink.delocalizedTracking"
              :target="mediaLink.targetAttribute"
              :to="mediaLink.url"
              @click="handlePromotionClick('title')"
            >
              <span v-if="$feature.showTitleHtml" v-html="titleHtml" />
              <span v-else>{{ title }}</span>
            </base-link>
            <template v-else>
              <span v-if="$feature.showTitleHtml" v-html="titleHtml" />
              <span v-else>{{ title }}</span>
            </template>
          </component>
          <p v-if="subtitle" v-style:c="subtitleColor" :class="subtitleStyle" data-test-id="cms-banner-text">
            <span v-if="$feature.showTitleHtml" v-html="subtitleHtml" />
            <span v-else>{{ subtitle }}</span>
          </p>
          <cms-rich-text v-if="richText" v-style:c="richTextColor || subtitleColor" :content="{ richText }" data-test-id="cms-banner-body" />
        </div>
        <div
          v-if="linkType !== 'No-CTA' && targets.length"
          class="flex-col gap-4 i-flex md:flex-row md:wrap lg:gap-6 not-first:pt-4 <md:w-full!"
          :class="[
            overlayWidth ? contentSize[overlayWidth] : '',
            ctaClass,
          ]"
          :style="`--cols: repeat(${targets.length},minmax(0,1fr))`"
        >
          <cms-shared-button
            v-for="(target, i) in targets"
            :key="i"
            v-bind="{ ...target, ...getTextLinkStyles(content) }"
            :size
            @click="handlePromotionClick(`cta${i + 1}`)"
          />
        </div>
        <div :class="overlayWidth ? contentSize[overlayWidth] : ''">
          <cms-rich-text
            v-if="richTextBelowTargets"
            v-style:c="richTextBelowTargetsColor || subtitleColor"
            :content="{ richText: richTextBelowTargets }"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { SectionProvider } from '#content/contexts/section'
import type {
  BannerSplitAlignmentLarge,
  BannerSplitAlignmentSmall,
  BannerSplitContent,
  BannerSplitMediaWidth,
  VerticalAlignment
} from '#types/components/cms/banner-split'
import type { TextAlignment } from '#types/components/cms/text-alignment'
import { SectionContextKey } from '#content/contexts/section'

const { content } = defineProps<{
  content: BannerSplitContent
}>()

const {
  alignmentLarge,
  alignmentSmall,
  backgroundColor,
  captionPosition,
  captionStyle,
  captionText,
  equalTargetSize,
  eyebrowLogo,
  eyebrowLogoResponsive,
  eyebrowText,
  eyebrowTextColor,
  hideTitle,
  imageWidth = '60',
  linkType,
  mediaList = [],
  mediaTarget,
  name,
  overlayWidth,
  overline,
  overlineColor,
  overlineStyle,
  promotionTracking,
  richText,
  richTextBelowTargets,
  richTextBelowTargetsColor,
  richTextColor,
  subtitle,
  subtitleHtml,
  subtitleColor,
  subtitleStyle,
  captionColor = subtitleColor,
  targets = [],
  textAlignmentLarge,
  textAlignmentSmall,
  title,
  titleHtml,
  titleColor,
  titleH1,
  titleStyle,
  verticalAlignment,
} = content

const { size } = useAppConfig().components.cms.sharedButton
const { getMedia } = useCms()
const { elementRef, handlePromotionClick } = usePromotionTracking(promotionTracking, name)

const { lazy } = inject(SectionContextKey, {} as SectionProvider)
const videoRefs = ref<HTMLVideoElement[]>([])
const mediaLink = mediaTarget || targets[0]
const responsiveMediaList = mediaList.map(getMedia)
const { images } = getMedia(eyebrowLogoResponsive)

const smallImageLayout = getAlignment(alignmentSmall) || 0
const imageLayout = getAlignment(alignmentLarge) || 0

const contentAlignmentLarge: Record<BannerSplitAlignmentLarge, string> = {
  'left-left': 'md:text-left md:order-first',
  'left-right': 'md:text-left md:order-last',
  'center-left': 'md:text-center md:order-first',
  'center-right': 'md:text-center md:order-last',
  'right-left': 'md:text-right md:order-first',
  'right-right': 'md:text-right md:order-last',
  '1-left': 'md:order-first',
  '1-right': 'md:order-last',
  '2-left': 'md:order-first',
  '2-right': 'md:order-last',
  '3-bottom-left': 'md:order-first',
  '3-bottom-right': 'md:order-last',
  '3-top-left': 'md:order-first',
  '3-top-right': 'md:order-last',
}

const contentAlignmentSmall: Partial<Record<BannerSplitAlignmentSmall, string>> = {
  left: '<md:text-left',
  center: '<md:text-center',
  right: '<md:text-right'
}

const ctaAlignmentLarge: Partial<Record<BannerSplitAlignmentLarge | TextAlignment, string>> = {
  'left-left': 'md:justify-start',
  'left-right': 'md:justify-start',
  'center-left': 'md:justify-center',
  'center-right': 'md:justify-center',
  'right-left': 'md:justify-end',
  'right-right': 'md:justify-end',
  'left': 'md:justify-start',
  'center': 'md:justify-center',
  'right': 'md:justify-end'
}

const ctaAlignmentSmall: Partial<Record<TextAlignment, string>> = {
  left: '<md:items-start',
  center: '<md:items-center',
  right: '<md:items-end'
}

const textAlignmentSmallClasses: Record<TextAlignment, string> = {
  left: '<md:items-start <md:text-left',
  center: '<md:items-center <md:text-center',
  right: '<md:items-end <md:text-right'
}

const textAlignmentLargeClasses: Record<TextAlignment, string> = {
  left: 'md:items-start md:text-left',
  center: 'md:items-center md:text-center',
  right: 'md:items-end md:text-right'
}

const verticalAlignmentClasses: Record<VerticalAlignment, string> = {
  top: 'md:justify-start',
  middle: 'md:justify-center',
  bottom: 'md:justify-end'
}

const hasCaption = captionText && captionPosition !== 'off'

const mediaSize: Record<BannerSplitMediaWidth, string> = {
  60: 'md:w-3/5',
  50: 'md:w-1/2',
  40: 'md:w-2/5'
}

const getMediaClasses = (index: number) => ({
  '<md:col-span-2': index === 0 && [3, 1, 0].includes(smallImageLayout),
  '<md:hidden': smallImageLayout && index > smallImageLayout - 1,
  'md:hidden': (imageLayout && (index > imageLayout - 1)) || (imageLayout === 3 && index > 0),
})

const equalTargetSizeClass = {
  '<md:grid <md:cols-$cols <md:cols-1 <md:auto-rows-fr': getValueForBreakpoint('sm', equalTargetSize),
  '~md:grid ~md:cols-$cols ~md:auto-rows-fr ~md:items-stretch': getValueForBreakpoint('md', equalTargetSize),
  'lg:grid lg:cols-$cols lg:auto-rows-fr lg:items-stretch': getValueForBreakpoint('lg', equalTargetSize)
}

const contentAlignment = [
  contentAlignmentLarge[alignmentLarge],
  contentAlignmentSmall[alignmentSmall],
]

const textAlignment = [
  textAlignmentSmallClasses[textAlignmentSmall],
  textAlignmentLargeClasses[textAlignmentLarge]
]

const ctaClass = [
  ctaAlignmentLarge[alignmentLarge],
  ctaAlignmentSmall[alignmentSmall],
  ctaAlignmentLarge[textAlignmentLarge],
  ctaAlignmentSmall[textAlignmentSmall],
  equalTargetSizeClass
]

const contentSize = {
  xs: 'w-10/12 md:w-1/3',
  sm: 'w-10/12 md:w-1/2',
  md: 'w-10/12 md:w-2/3',
  lg: 'w-full md:w-11/12'
}

const titleTag = titleH1 ? 'h1' : 'h2'

onMounted(() => videoRefs.value.forEach(({ play }) => play()))
</script>
